import { useCallback, useSyncExternalStore } from "react";

/**
 * "Preview as member" mode — a local-only flag that lets admins see the
 * product the way an ordinary (non-admin, non-LifeMart) member does.
 *
 * Nothing changes in the database: the real admin role and any real LifeMart
 * verification stay intact. The flag lives in localStorage and only affects
 * what the UI renders in this browser.
 */

const STORAGE_KEY = "ha_member_preview";
const CHANGE_EVENT = "ha-member-preview-changed";

const readFlag = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const writeFlag = (value: boolean) => {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, "true");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }
};

const subscribe = (callback: () => void) => {
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
};

/**
 * URL escape hatch: /dashboard?member=1 turns preview on, ?member=0 turns it off.
 * Handy when the account-menu toggle isn't reachable (e.g. co-branded shells).
 */
if (typeof window !== "undefined") {
  const param = new URLSearchParams(window.location.search).get("member");
  if (param === "1" && !readFlag()) writeFlag(true);
  if (param === "0" && readFlag()) writeFlag(false);
}

export const useMemberPreview = () => {
  const isPreviewing = useSyncExternalStore(
    subscribe,
    readFlag,
    () => false, // server snapshot — never preview during SSR
  );

  const enable = useCallback(() => writeFlag(true), []);
  const disable = useCallback(() => writeFlag(false), []);

  return { isPreviewing, enable, disable };
};
